import React, { CSSProperties } from "react";
import classNames from "classnames";
import "./widgetFrame.css";
import readableTextColor from "../../../helpers/readableTextColor";

type Props = {
  /** Rendered verbatim as the element id (channel widgets use `widget-<id>`). */
  id?: string;
  title?: string | null;
  /** Per-widget color override; falls back to the context's theme color when null. */
  titleBackgroundOverride?: string | null;
  hideTitle?: boolean;
  /**
   * Drop the title bar when there is no title instead of rendering an empty
   * colored bar. Channel widgets require a title, info-leiste widgets do not.
   */
  hideTitleWhenBlank?: boolean;
  /** Rendered before the title inside the title bar (channel focus label). */
  titleAccessory?: React.ReactNode;
  /** Rendered after the title, at the end of the title bar (widget controls). */
  titleEnd?: React.ReactNode;
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
  style?: CSSProperties;
  containerRef?: React.RefObject<HTMLDivElement>;
  children?: React.ReactNode;
};

/**
 * The visual shell of channel widgets (components/channels/widgets/
 * WidgetContainer), on channels and on the info bars alike: frame + title bar
 * with a network-derived background that can be overridden per widget.
 */
export default function WidgetFrame({
  id,
  title,
  titleBackgroundOverride,
  hideTitle = false,
  hideTitleWhenBlank = false,
  titleAccessory,
  titleEnd,
  className,
  titleClassName,
  contentClassName,
  style,
  containerRef,
  children,
}: Props) {
  const showTitle = !hideTitle && !(hideTitleWhenBlank && !title);

  return (
    <div
      id={id}
      ref={containerRef}
      className={classNames("widget-frame", className)}
      style={style}
    >
      {showTitle && (
        <div
          className={classNames(
            "widget-frame__title flex items-center px-4 font-semibold text-sm rounded-tl rounded-tr gap-2",
            titleClassName,
          )}
          style={
            titleBackgroundOverride
              ? {
                  backgroundColor: titleBackgroundOverride,
                  color:
                    readableTextColor(titleBackgroundOverride) ||
                    "var(--normal-text)",
                }
              : undefined
          }
        >
          {titleAccessory}
          <span className="line-clamp-2">{title}</span>
          {titleEnd}
        </div>
      )}
      <div
        className={classNames(
          "widget-frame__content rounded-bl rounded-br",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}
